import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const fmt = (n) => "₹" + Number(n).toLocaleString("en-IN");

const Stars = ({ value }) => (
  <span style={{ fontSize: ".8rem", letterSpacing: "1px" }}>
    {[1, 2, 3, 4, 5].map((s) => (
      <span key={s} style={{ color: s <= Math.round(value) ? "#d4a017" : "var(--c-border)" }}>★</span>
    ))}
  </span>
);

/* ─────────────────────────────────────────────
   TOAST
───────────────────────────────────────────── */
function useToast() {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);

  const show = (msg, type = "success") => {
    clearTimeout(timerRef.current);
    setToast({ msg, type });
    timerRef.current = setTimeout(() => setToast(null), 3000);
  };

  return { toast, show };
}

/* ─────────────────────────────────────────────
   CART ITEM ROW
   Note: a single product can produce two rows when an offer
   (e.g. Buy 4 Get 1 Free) is active — one paid row, one bonus
   row with item.isFree = true. That's intended by the offer
   engine, not a duplication bug. The badge below makes the
   bonus row visually obvious instead of looking like a dupe.
───────────────────────────────────────────── */
function CartItem({ item, onQtyChange, onRemove }) {
  const [removing, setRemoving] = useState(false);

  const handleRemove = () => {
    setRemoving(true);
    setTimeout(() => onRemove(item.id), 320);
  };

  const unitPrice = item.displayPrice ?? item.price;

  return (
    <div className={`ci ${removing ? "ci--removing" : ""} ${item.isFree ? "ci--free" : ""}`}>
      {/* Product image / emoji */}
      <div className="ci__img">
        {item.emoji?.url ? (
          <img src={item.emoji.url} alt={item.name} style={{ width: "fit-content" }} />
        ) : (
          <span className="ci__emoji">{item.emoji || "🌿"}</span>
        )}
      </div>

      {/* Details */}
      <div className="ci__details">
        {item.category && <span className="ci__cat">{item.category}</span>}
        <h3 className="ci__name">
          {item.name}
          {item.isFree && (
            <span className="ci__free-badge">
              {item.discountPercent > 0 && item.discountPercent < 100
                ? `🎉 ${item.discountPercent}% OFF`
                : "🎁 Free Gift"}
              {item.offerApplied ? ` — ${item.offerApplied}` : ""}
            </span>
          )}
        </h3>
        {item.rating && <Stars value={item.rating} />}
        <span className="ci__unit-price">
          {item.isFree && item.originalPrice > unitPrice && (
            <s style={{ marginRight: 6, opacity: 0.6 }}>{fmt(item.originalPrice)}</s>
          )}
          {fmt(unitPrice)} each
        </span>
      </div>

      {/* Qty stepper — disabled on bonus rows since their qty is offer-controlled */}
      <div className="ci__qty">
        <button
          className="ci__qty-btn"
          onClick={() => onQtyChange(item.id, item.quantity - 1)}
          aria-label="Decrease quantity"
          disabled={item.quantity <= 1 || item.isFree}
        >−</button>
        <span className="ci__qty-val">{item.quantity}</span>
        <button
          className="ci__qty-btn"
          onClick={() => onQtyChange(item.id, item.quantity + 1)}
          aria-label="Increase quantity"
          disabled={item.quantity >= 20 || item.isFree}
        >+</button>
      </div>

      {/* Line total */}
      <div className="ci__total">
        <span className="ci__total-val">{fmt(unitPrice * item.quantity)}</span>
      </div>

      {/* Remove */}
      <button className="ci__remove" onClick={handleRemove} aria-label="Remove item">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   COUPON INPUT
───────────────────────────────────────────── */
function CouponInput({ applyCoupon, removeCoupon, coupon }) {
  const [code, setCode] = useState("");
  const [state, setState] = useState(null); // { ok, msg }

  const handleApply = () => {
    if (!code.trim()) return;
    const result = applyCoupon(code);
    setState({ ok: result.success, msg: result.message });
    if (result.success) setCode("");
  };

  const handleRemove = () => {
    removeCoupon();
    setCode("");
    setState(null);
  };

  if (coupon) {
    return (
      <div className="coupon coupon--applied">
        <span className="coupon__tag">🎉 <strong>{coupon.code}</strong> — {coupon.label}</span>
        <button className="coupon__remove" onClick={handleRemove}>Remove</button>
      </div>
    );
  }

  return (
    <div className="coupon">
      <div className="coupon__row">
        <input
          className="coupon__input"
          type="text"
          placeholder="Enter coupon code…"
          value={code}
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setState(null); }}
          onKeyDown={(e) => e.key === "Enter" && handleApply()}
        />
        <button className="coupon__btn" onClick={handleApply} disabled={!code.trim()}>Apply</button>
      </div>
      {state && (
        <p className={`coupon__msg ${state.ok ? "coupon__msg--ok" : "coupon__msg--err"}`}>
          {state.ok ? "✓" : "✕"} {state.msg}
        </p>
      )}
      <p className="coupon__hint">Try: <code>SANJIVANI10</code> · <code>AYUR50</code> · <code>WELLNESS20</code></p>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ORDER SUMMARY
───────────────────────────────────────────── */
function OrderSummary({
  subtotal, discount, shipping, total,
  itemCount, coupon, FREE_SHIPPING_THRESHOLD, amountForFreeShipping,
  onCheckout,
}) {
  return (
    <aside className="summary">
      <h2 className="summary__title">Order Summary</h2>

      {/* Free shipping progress */}
      {/* {amountForFreeShipping > 0 && (
        <div className="free-ship">
          <p className="free-ship__text">
            Add <strong>{fmt(amountForFreeShipping)}</strong> more for free shipping!
          </p>
          <div className="free-ship__bar">
            <div
              className="free-ship__fill"
              style={{ width: `${Math.min(100, ((subtotal / FREE_SHIPPING_THRESHOLD) * 100))}%` }}
            />
          </div>
        </div>
      )} */}

      <div className="summary__rows">
        <div className="summary__row">
          <span>Subtotal ({itemCount} item{itemCount !== 1 ? "s" : ""})</span>
          <span>{fmt(subtotal)}</span>
        </div>
        {discount > 0 && (
          <div className="summary__row summary__row--discount">
            <span>Discount ({coupon?.code})</span>
            <span>−{fmt(discount)}</span>
          </div>
        )}
        <div className="summary__row">
          <span>Shipping</span>
          <span>{shipping === 0 ? <span className="free-tag">FREE</span> : fmt(shipping)}</span>
        </div>
        <div className="summary__divider" />
        <div className="summary__row summary__row--total">
          <span>Total</span>
          <span style={{ fontFamily: 'serif' }}>{fmt(total)}</span>
        </div>
      </div>

      <button className="checkout-btn" onClick={onCheckout} disabled={itemCount === 0}>
        Proceed to Checkout →
      </button>
      <p className="summary__secure">🔒 Secured by Razorpay · UPI, Cards, Wallets</p>

      {/* Accepted payments */}
      <div className="summary__payments">
        {["UPI", "Visa", "MC", "NetB", "Wallet"].map((p) => (
          <span className="payment-chip" key={p}>{p}</span>
        ))}
      </div>
    </aside>
  );
}

/* ─────────────────────────────────────────────
   EMPTY CART
───────────────────────────────────────────── */
function EmptyCart() {
  return (
    <div className="empty">
      <div className="empty__visual">
        <span className="empty__icon">🛒</span>
        <div className="empty__ring" />
      </div>
      <h2 className="empty__title">Your cart is empty</h2>
      <p className="empty__sub">
        Explore our collection of pure Ayurvedic formulations crafted for your wellbeing.
      </p>
      <Link to="/products" className="empty__cta">
        Shop Products →
      </Link>
    </div>
  );
}

/* ─────────────────────────────────────────────
   CART PAGE
───────────────────────────────────────────── */
export default function Cart() {
  const {
    items, coupon,
    subtotal, discount, shipping, total, itemCount,
    amountForFreeShipping, FREE_SHIPPING_THRESHOLD,
    addItem, removeItem, updateQty, clearCart, applyCoupon, removeCoupon,
  } = useCart();

  const navigate = useNavigate();
  const { toast, show: showToast } = useToast();
  const [clearing, setClearing] = useState(false);

  const handleQtyChange = (id, qty) => {
    updateQty(id, qty);
    if (qty < 1) showToast("Item removed from cart.");
  };

  const handleRemove = (id) => {
    const item = items.find((i) => i.id === id);
    removeItem(id);
    showToast(`"${item?.name}" removed.`);
  };

  const handleClear = () => {
    setClearing(true);
    setTimeout(() => { clearCart(); setClearing(false); }, 350);
    showToast("Cart cleared.");
  };

  const handleCheckout = () => {
    showToast("Redirecting to checkout…");
    setTimeout(() => navigate("/checkout"), 900);
  };

  return (
    <>
      <style>{css}</style>

      {/* Toast */}
      {toast && <div className={`toast toast--${toast.type}`}>{toast.msg}</div>}

      <div className="cart-page">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <div className="container">
            <Link to="/">Home</Link><span>›</span>
            <Link to="/products">Products</Link><span>›</span>
            <span className="bc-cur">Cart</span>
          </div>
        </div>

        {/* Header */}
        <div className="cart-header">
          <div className="container cart-header__inner">
            <div>
              <h1 className="cart-header__title">Your Cart</h1>
              <p className="cart-header__sub">
                {itemCount > 0
                  ? `${itemCount} item${itemCount !== 1 ? "s" : ""} ready for checkout`
                  : "Nothing here yet — let's change that"}
              </p>
            </div>

          </div>
        </div>

        <div className="container cart-layout">

          {/* Empty state */}
          {items.length === 0 && <EmptyCart />}

          {/* Filled cart */}
          {items.length > 0 && (
            <>
              {/* Items + Coupon column */}
              <div className="cart-left">

                {/* Item list */}
                <div className="items-section">
                  <div className="items-header">
                    <span>Product</span>
                    <span className="items-header__qty">Qty</span>
                    <span className="items-header__total">Total</span>
                  </div>
                  <div className={`items-list ${clearing ? "items-list--clearing" : ""}`}>
                    {items.map((item, i) => (
                      <CartItem
                        key={item.id}
                        item={item}
                        style={{ "--delay": `${i * 0.06}s` }}
                        onQtyChange={handleQtyChange}
                        onRemove={handleRemove}
                      />
                    ))}
                  </div>
                </div>

                {/* Continue shopping */}
                <div className="continue-row">
                  <Link to="/products" className="continue-btn">
                    ← Continue Shopping
                  </Link>
                </div>
              </div>

              {/* Summary column */}
              <OrderSummary
                subtotal={subtotal}
                discount={discount}
                shipping={shipping}
                total={total}
                itemCount={itemCount}
                coupon={coupon}
                FREE_SHIPPING_THRESHOLD={FREE_SHIPPING_THRESHOLD}
                amountForFreeShipping={amountForFreeShipping}
                onCheckout={handleCheckout}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   CSS
───────────────────────────────────────────── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=Jost:wght@300;400;500;600&display=swap');

:root {
  --c-primary:       #2d6a4f;
  --c-primary-light: #40916c;
  --c-primary-dark:  #1b4332;
  --c-accent:        #d4a017;
  --c-accent-soft:   #fef3c7;
  --c-bg:            #f9f6f0;
  --c-surface:       #ffffff;
  --c-text:          #1c2b22;
  --c-muted:         #5a7262;
  --c-border:        #d4e6d5;
  --c-error:         #c0392b;
  --c-success:       #276749;
  --f-display:       'Cormorant Garamond', Georgia, serif;
  --f-body:          'Jost', sans-serif;
  --radius:          14px;
  --radius-sm:       8px;
  --shadow:          0 4px 24px rgba(27,67,50,.09);
  --shadow-lg:       0 12px 40px rgba(27,67,50,.14);
  --ease:            cubic-bezier(.4,0,.2,1);
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: var(--f-body); background: var(--c-bg); color: var(--c-text); }
a { text-decoration: none; color: inherit; }
.container { max-width: 1160px; margin: 0 auto; padding: 0 24px; }

/* ── TOAST ── */
.toast {
  position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%);
  background: var(--c-primary-dark); color: #fff;
  font-family: var(--f-body); font-size: .84rem; font-weight: 500;
  padding: 12px 24px; border-radius: 100px;
  box-shadow: 0 8px 32px rgba(0,0,0,.2); z-index: 9999; white-space: nowrap;
  animation: toastIn .3s var(--ease);
}
.toast--error { background: var(--c-error); }
@keyframes toastIn { from{opacity:0;transform:translateX(-50%) translateY(10px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }
.btn-primary {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  font-family: var(--f-body); font-size: .82rem; font-weight: 600;
  letter-spacing: .07em; text-transform: uppercase;
  padding: 12px 28px; border-radius: 100px; border: none;
  background: var(--c-primary); color: #fff; cursor: pointer;
  transition: background .22s, transform .22s;
}
.btn-primary:hover:not(:disabled) { background: var(--c-primary-dark); transform: translateY(-1px); }
.btn-primary:disabled { opacity: .7; cursor: not-allowed; }
/* ── BREADCRUMB ── */
.breadcrumb {
  background: var(--c-surface); border-bottom: 1px solid var(--c-border);
  padding: 13px 0; font-size: .78rem; color: var(--c-muted);
}
.breadcrumb .container { display: flex; align-items: center; gap: 8px; }
.breadcrumb a { color: var(--c-primary); transition: color .2s; }
.breadcrumb a:hover { color: var(--c-primary-dark); }
.bc-cur { color: var(--c-text); font-weight: 600; }

/* ── CART HEADER ── */
.cart-header {
  background: linear-gradient(135deg, var(--c-primary-dark), #1e4d35);
  padding: 40px 0 36px;
  position: relative; overflow: hidden;
  display:'flex',
  justify-content:'center'
}
.cart-header::before {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(ellipse 70% 100% at 90% 50%, rgba(64,145,108,.2), transparent 60%);
}
.cart-header__inner {
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 16px; flex-wrap: wrap;
}
.cart-header__title {
  font-family: var(--f-display); font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 600; color: #fff; line-height: 1.1; margin-bottom: 6px;
}
.cart-header__sub { font-size: .88rem; color: rgba(255,255,255,.6); font-weight: 300; }

.clear-all-btn {
  font-family: var(--f-body); font-size: .76rem; font-weight: 600;
  letter-spacing: .06em; text-transform: uppercase;
  padding: 9px 20px; border-radius: 100px;
  background: rgba(255,255,255,.1); color: rgba(255,255,255,.8);
  border: 1px solid rgba(255,255,255,.2); cursor: pointer;
  transition: background .22s, color .22s;
}
.clear-all-btn:hover { background: rgba(192,57,43,.35); color: #fff; border-color: rgba(192,57,43,.5); }

/* ── LAYOUT ── */
.cart-page { min-height: 100vh; }
.cart-layout {
  display: grid; grid-template-columns: 1fr 360px;
  gap: 32px; padding-top: 36px; padding-bottom: 72px;
  align-items: start;
}
.cart-left { display: flex; flex-direction: column; gap: 20px; }

/* ══════════════════════════════
   CART ITEMS
══════════════════════════════ */
.items-section {
  background: var(--c-surface); border: 1px solid var(--c-border);
  border-radius: var(--radius); overflow: hidden;
}
.items-header {
  display: grid; grid-template-columns: 1fr 120px 100px 36px;
  gap: 16px; padding: 14px 20px;
  background: var(--c-bg); border-bottom: 1px solid var(--c-border);
  font-size: .68rem; font-weight: 700; letter-spacing: .12em; text-transform: uppercase;
  color: var(--c-muted);
}
.items-header__qty  { text-align: center; }
.items-header__total { text-align: right; }

.items-list { padding: 8px 0; }
.items-list--clearing { opacity: 0; transition: opacity .3s; }

/* ── CART ITEM ── */
.ci {
  display: grid; grid-template-columns: 76px 1fr 120px 100px 36px;
  gap: 16px; align-items: center;
  padding: 16px 20px; border-bottom: 1px solid var(--c-border);
  transition: opacity .32s var(--ease), transform .32s var(--ease);
  animation: ciIn .4s var(--ease) both;
  animation-delay: var(--delay, 0s);
}
@keyframes ciIn { from{opacity:0;transform:translateX(-12px)} to{opacity:1;transform:none} }
.ci:last-child { border-bottom: none; }
.ci--removing { opacity: 0; transform: translateX(-24px); }
.ci--free { background: rgba(212,160,23,.05); }

.ci__img {
  width: 76px; height: 76px; border-radius: 10px; overflow: hidden;
  background: linear-gradient(135deg, #f0f7f2, #e8f5ec);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.ci__img img { width: 100%; height: 100%; object-fit: cover; }
.ci__emoji { font-size: 2.4rem; }

.ci__details { min-width: 0; }
.ci__cat {
  display: block; font-size: .64rem; font-weight: 600;
  letter-spacing: .1em; text-transform: uppercase;
  color: var(--c-primary-light); margin-bottom: 3px;
}
.ci__name {
  font-family: var(--f-display); font-size: 1.1rem; font-weight: 600;
  color: var(--c-primary-dark); white-space: nowrap; overflow: hidden;
  text-overflow: ellipsis; margin-bottom: 4px;
}
.ci__free-badge {
  display: inline-block; margin-left: 8px; font-size: .68rem; font-weight: 600;
  font-family: var(--f-body); background: var(--c-accent-soft); color: #92660a;
  padding: 2px 9px; border-radius: 100px; vertical-align: middle;
  white-space: nowrap;
}
.ci__unit-price { font-size: .76rem; color: var(--c-muted); display: block; margin-top: 5px; }

/* Qty stepper */
.ci__qty {
  display: flex; align-items: center; justify-content: center;
  border: 1.5px solid var(--c-border); border-radius: 100px;
  overflow: hidden; width: fit-content; margin: 0 auto;
}
.ci__qty-btn {
  width: 32px; height: 32px; background: var(--c-bg); border: none;
  font-size: 1rem; cursor: pointer; color: var(--c-primary-dark);
  transition: background .2s; display: flex; align-items: center; justify-content: center;
}
.ci__qty-btn:hover:not(:disabled) { background: var(--c-border); }
.ci__qty-btn:disabled { color: var(--c-border); cursor: not-allowed; }
.ci__qty-val { width: 36px; text-align: center; font-weight: 600; font-size: .9rem; }

.ci__total { text-align: right; }
.ci__total-val {  font-size: 1.2rem; font-weight: 600; color: var(--c-primary-dark); }

.ci__remove {
  width: 32px; height: 32px; border-radius: 50%;
  border: 1px solid var(--c-border); background: var(--c-bg);
  cursor: pointer; color: var(--c-muted);
  display: flex; align-items: center; justify-content: center;
  transition: all .22s; flex-shrink: 0;
}
.ci__remove:hover { background: #fff1f0; border-color: #fca5a5; color: var(--c-error); }

/* ══════════════════════════════
   COUPON SECTION
══════════════════════════════ */
.coupon-section {
  background: var(--c-surface); border: 1px solid var(--c-border);
  border-radius: var(--radius); padding: 24px 28px;
}
.coupon-section__title {
  font-size: .82rem; font-weight: 700; letter-spacing: .06em;
  text-transform: uppercase; color: var(--c-muted); margin-bottom: 16px;
}

.coupon--applied {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; background: rgba(45,106,79,.06);
  border: 1px solid rgba(45,106,79,.2); border-radius: var(--radius-sm);
  padding: 12px 16px;
}
.coupon__tag { font-size: .88rem; color: var(--c-primary-dark); }
.coupon__remove {
  font-family: var(--f-body); font-size: .74rem; font-weight: 600;
  background: none; border: 1px solid var(--c-border);
  color: var(--c-muted); padding: 4px 12px; border-radius: 100px; cursor: pointer;
  transition: all .2s;
}
.coupon__remove:hover { background: var(--c-error); color: #fff; border-color: var(--c-error); }

.coupon__row { display: flex; gap: 10px; }
.coupon__input {
  flex: 1; font-family: var(--f-body); font-size: .88rem;
  padding: 10px 16px; border: 1.5px solid var(--c-border);
  border-radius: 100px; outline: none; color: var(--c-text);
  background: var(--c-bg); transition: border-color .22s, box-shadow .22s;
  text-transform: uppercase; letter-spacing: .06em;
}
.coupon__input:focus { border-color: var(--c-primary-light); box-shadow: 0 0 0 3px rgba(64,145,108,.12); }
.coupon__input::placeholder { text-transform: none; letter-spacing: 0; color: var(--c-muted); }
.coupon__btn {
  font-family: var(--f-body); font-size: .8rem; font-weight: 600;
  letter-spacing: .06em; text-transform: uppercase;
  padding: 10px 22px; border-radius: 100px; border: none;
  background: var(--c-primary); color: #fff; cursor: pointer;
  transition: background .22s, transform .22s;
  white-space: nowrap;
}
.coupon__btn:hover:not(:disabled) { background: var(--c-primary-dark); transform: translateY(-1px); }
.coupon__btn:disabled { background: var(--c-border); color: var(--c-muted); cursor: not-allowed; }
.coupon__msg {
  font-size: .78rem; font-weight: 500; margin-top: 9px; padding: 6px 12px;
  border-radius: var(--radius-sm);
}
.coupon__msg--ok  { color: var(--c-success); background: rgba(39,103,73,.08); }
.coupon__msg--err { color: var(--c-error); background: rgba(192,57,43,.07); }
.coupon__hint { font-size: .74rem; color: var(--c-muted); margin-top: 10px; }
.coupon__hint code {
  background: var(--c-bg); border: 1px solid var(--c-border);
  padding: 2px 7px; border-radius: 4px; font-size: .72rem;
  color: var(--c-primary); font-family: monospace;
}

/* ── CONTINUE BTN ── */
.continue-row { display: flex; }
.continue-btn {
  font-family: var(--f-body); font-size: .82rem; font-weight: 600;
  letter-spacing: .05em; color: var(--c-primary);
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 0; transition: gap .2s;
}
.continue-btn:hover { gap: 10px; }

/* ══════════════════════════════
   ORDER SUMMARY
══════════════════════════════ */
.summary {
  background: var(--c-surface); border: 1px solid var(--c-border);
  border-radius: var(--radius); padding: 28px;
  position: sticky; top: 24px;
  animation: summaryIn .5s var(--ease) both;
}
@keyframes summaryIn { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:none} }

.summary__title {
  font-family: var(--f-display); font-size: 1.6rem; font-weight: 600;
  color: var(--c-primary-dark); margin-bottom: 20px;
}

/* Free shipping bar */
.free-ship { margin-bottom: 20px; }
.free-ship__text { font-size: .8rem; color: var(--c-muted); margin-bottom: 8px; }
.free-ship__text strong { color: var(--c-primary-dark); }
.free-ship__bar {
  height: 7px; background: var(--c-border); border-radius: 100px; overflow: hidden;
}
.free-ship__fill {
  height: 100%; background: linear-gradient(90deg, var(--c-primary-light), var(--c-primary));
  border-radius: 100px; transition: width .5s var(--ease);
}
.free-ship__achieved {
  font-size: .82rem; font-weight: 600; color: var(--c-primary);
  background: rgba(45,106,79,.08); border: 1px solid rgba(45,106,79,.2);
  padding: 10px 14px; border-radius: var(--radius-sm); margin-bottom: 20px;
}

/* Summary rows */
.summary__rows { margin-bottom: 24px; }
.summary__row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 0; font-size: .88rem; color: var(--c-muted);
  border-bottom: 1px dashed var(--c-border);
}
.summary__row:last-child { border-bottom: none; }
.summary__row--discount { color: var(--c-success); }
.summary__row--total {
  font-size: 1rem; font-weight: 700;
  color: var(--c-primary-dark); padding-top: 16px;
  border-top: 2px solid var(--c-border); border-bottom: none; margin-top: 6px;
}
.summary__row--total span:last-child {
  font-family: var(--f-display); font-size: 1.6rem; font-weight: 600;
}
.summary__divider { height: 0; }
.free-tag {
  background: rgba(45,106,79,.08); color: var(--c-primary);
  font-size: .7rem; font-weight: 700; letter-spacing: .08em;
  text-transform: uppercase; padding: 3px 10px; border-radius: 100px;
}

/* Checkout */
.checkout-btn {
  width: 100%; font-family: var(--f-body); font-size: .88rem;
  font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
  padding: 16px; border-radius: 100px; border: none; cursor: pointer;
  background: var(--c-primary); color: #fff;
  box-shadow: 0 6px 24px rgba(45,106,79,.32);
  transition: background .22s, transform .22s, box-shadow .22s;
  margin-bottom: 12px;
}
.checkout-btn:hover:not(:disabled) {
  background: var(--c-primary-dark); transform: translateY(-2px);
  box-shadow: 0 10px 32px rgba(45,106,79,.4);
}
.checkout-btn:disabled { background: var(--c-border); color: var(--c-muted); cursor: not-allowed; box-shadow: none; }
.summary__secure { font-size: .72rem; color: var(--c-muted); text-align: center; margin-bottom: 16px; }
.summary__payments { display: flex; gap: 7px; flex-wrap: wrap; justify-content: center; }
.payment-chip {
  font-size: .68rem; font-weight: 600; background: var(--c-bg);
  border: 1px solid var(--c-border); color: var(--c-muted);
  padding: 4px 10px; border-radius: var(--radius-sm); letter-spacing: .04em;
}

/* ══════════════════════════════
   EMPTY STATE
══════════════════════════════ */
.empty {
  grid-column: 1 / -1; text-align: center; padding: 80px 24px 60px;
  animation: fadeUp .5s var(--ease);
}
@keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
.empty__visual {
  position: relative; width: 120px; height: 120px;
  margin: 0 auto 28px; display: flex; align-items: center; justify-content: center;
}
.empty__icon { font-size: 4rem; position: relative; z-index: 1; }
.empty__ring {
  position: absolute; inset: 0; border-radius: 50%;
  border: 2px dashed var(--c-border);
  animation: ringPulse 3s ease-in-out infinite;
}
@keyframes ringPulse { 0%,100%{transform:scale(1);opacity:.6} 50%{transform:scale(1.08);opacity:1} }
.empty__title {
  font-family: var(--f-display); font-size: 2.2rem; font-weight: 600;
  color: var(--c-primary-dark); margin-bottom: 12px;
}
.empty__sub { font-size: .96rem; color: var(--c-muted); font-weight: 300; max-width: 400px; margin: 0 auto 28px; }
.empty__cta {
  display: inline-flex; align-items: center;
  font-family: var(--f-body); font-size: .85rem; font-weight: 700;
  letter-spacing: .08em; text-transform: uppercase;
  background: var(--c-primary); color: #fff;
  padding: 14px 32px; border-radius: 100px;
  box-shadow: 0 6px 20px rgba(45,106,79,.3);
  transition: background .22s, transform .22s;
}
.empty__cta:hover { background: var(--c-primary-dark); transform: translateY(-2px); }

.empty__suggest { margin-top: 48px; }
.empty__suggest-title {
  font-size: .72rem; font-weight: 700; letter-spacing: .14em;
  text-transform: uppercase; color: var(--c-muted); margin-bottom: 14px;
}
.empty__chips { display: flex; flex-wrap: wrap; gap: 9px; justify-content: center; }
.empty__chip {
  font-size: .82rem; background: var(--c-surface);
  border: 1px solid var(--c-border); color: var(--c-primary);
  padding: 8px 18px; border-radius: 100px;
  transition: all .22s;
}
.empty__chip:hover { background: var(--c-primary); color: #fff; border-color: var(--c-primary); }

/* ── RESPONSIVE ── */
@media (max-width: 900px) {
  .cart-layout { grid-template-columns: 1fr; }
  .summary { position: static; }
}
@media (max-width: 640px) {
  .ci { grid-template-columns: 60px 1fr 90px; gap: 12px; }
  .ci__total { display: none; }
  .ci__remove { justify-self: end; }
  .items-header { grid-template-columns: 60px 1fr 90px 32px; }
  .items-header__total { display: none; }
  .cart-header__inner { flex-direction: column; align-items: flex-start; }
}
@media (max-width: 440px) {
  .ci { grid-template-columns: 56px 1fr 36px ; gap: 8px; }
  .ci__qty { display: none; }
  .items-header { grid-template-columns: 56px 1fr 36px; }
  .items-header__qty { display: none; }
}
`;